import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { FileData } from '@uzurv/core';
import { UZURV_REST_CONFIGURATION, UzurvRestConfiguration, UzurvRestService } from '@uzurv/http';
import { Deserialize, Serialize } from 'cerialize';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { FileUpload, Itinerary, RideRequest, RideRequestLocation } from '../../models';

@Injectable({
  providedIn: 'root'
})
export class ItineraryService extends UzurvRestService {
  readonly resourceUrl = 'itineraries/';

  constructor(
    httpClient: HttpClient,
    @Inject(UZURV_REST_CONFIGURATION)
    protected uzurvRestConfiguration: UzurvRestConfiguration,
    domSanitizer: DomSanitizer
  ) {
    super(httpClient, uzurvRestConfiguration, domSanitizer);
  }

  /**
   * @param id
   * @permission itinerary_read
   */
  findById(id: number): Observable<Itinerary> {
    return this.get<Itinerary>(this.resourceUrl + id).pipe(map(response => Deserialize(response, Itinerary)));
  }

  /**
   * Saves a new itinerary
   *
   * @param itinerary
   * @permission itinerary_create
   */
  save(itinerary: Itinerary): Observable<Itinerary> {
    return this.post<Itinerary>(this.resourceUrl, Serialize(itinerary)).pipe(map(response => Deserialize(response, Itinerary)));
  }

  /**
   * @param file
   * @permission itinerary_file_upload
   */
  uploadItineraryCSV(file: File) {
    return this.uploadWithProgress(`${this.resourceUrl}fileUpload/`, file);
  }

  /**
   * @permission itinerary_file_upload
   */
  getItineraryUploadStatuses(pageNumber = 0, pageSize = 1000): Observable<Array<FileUpload>> {
    return this.get<Array<FileUpload>>(`${this.resourceUrl}fileUploads/`, { pageNumber: pageNumber, pageSize: pageSize }).pipe(
      map(response => Deserialize(response, FileUpload))
    );
  }

  /**
   * @permission itinerary_file_upload
   */
  getItineraryUploadErrorFile(fileId: number): Observable<FileData> {
    return this.get<FileData>(`${this.resourceUrl}fileUploads/${fileId}/errorCsv`).pipe(map(response => Deserialize(response, FileData)));
  }

  /**
   * Patch a ride request. Use this to activate a will call ride by setting activated to `true`
   *
   * @param itineraryId
   * @param rideRequestId
   * @param patchDict
   * @permission ride_request_update
   */
  patchRideRequest(
    itineraryId: number,
    rideRequestId: number,
    patchDict: {
      activated?: boolean;
      riderNote?: string;
      pickupDate?: Date;
      pickupLocation?: RideRequestLocation;
      dropoffLocation?: RideRequestLocation;
    }
  ): Observable<RideRequest> {
    patchDict['itineraryId'] = itineraryId;
    patchDict['id'] = rideRequestId;
    return this.patch(`${this.resourceUrl}${itineraryId}/rideRequests/${rideRequestId}`, patchDict).pipe(
      map(response => Deserialize(response, RideRequest))
    );
  }

  /**
   * Cancels a ride request in an itinerary
   *
   * @param itineraryId
   * @param rideRequestId
   * @param cancellationReasonId
   * @permission ride_cancel
   */
  cancelRideRequest(itineraryId: number, rideRequestId: number, cancellationReasonId?: number): Observable<boolean> {
    return this.delete<boolean>(
      `${this.resourceUrl}${itineraryId}/rideRequests/${rideRequestId}`,
      cancellationReasonId ? { cancellationReasonId: cancellationReasonId.toString() } : null
    );
  }

  /**
   * Cancels a ride request in an itinerary for a Web Self Service specific user
   *
   * @param itineraryId
   * @param rideRequestId
   * @param cancellationReasonId
   * @permission web_self_service_ride_cancel
   */
  webSelfServiceCancelRideRequest(itineraryId: number, rideRequestId: number, cancellationReasonId?: number): Observable<boolean> {
    return this.delete<boolean>(
      `${this.resourceUrl}${itineraryId}/rideRequests/${rideRequestId}/webSelfServiceCancel/`,
      cancellationReasonId ? { cancellationReasonId: cancellationReasonId.toString() } : null
    );
  }
}
